import React, { useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { compact, isEmpty, omit } from 'lodash';

import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import LoadingIndicator from '~/components/LoadingIndicator';
import { MultiSelectTextFieldFormik, TextFieldFormik as TextFieldFormikUntyped } from '~/components/TextFieldFormik';
import useDataFetcher from '~/components/useDataFetcher';
import type { SlaTemplateMetadata, SlaTemplateParameterValues } from '~/server_shared/openapi/data-contracts';

import { slaTemplateMetadataUrl, slaTemplateParamValuesUrl } from '../api/slaRulesApi';
import { TEXTS } from '../texts';
import type { SlaTemplateParameter } from '../types';

import type { SlaRuleFormValues } from './formValues';
import { contextParameters, isEmptySelection } from './formValues';

interface TextFieldFormikProps {
  id: string;
  label: string;
  type?: 'number';
  value?: unknown;
  helperText?: string;
  fullWidth?: boolean;
}
// Assertion over a still-untyped JS component, which the TS rule allows for gradual migration: it
// only narrows the props this file passes, so a wrong prop name or type is still a compile error.
const TextFieldFormik = TextFieldFormikUntyped as React.ComponentType<TextFieldFormikProps>;

interface ContextParamsFieldsProps {
  organizationId: number;
  cardId: number;
  /** Called once per card, when its metadata arrives, with every parameter the card declares. */
  onParametersLoaded: (parameters: SlaTemplateParameter[]) => void;
}

interface ContextParamFieldProps {
  organizationId: number;
  cardId: number;
  parameter: SlaTemplateParameter;
}

interface ParamOption {
  value: unknown;
  label: string;
}

/** The stored value as the multi-select shows it: a stored scalar as its one-element list, which the
 *  form keeps as the scalar until the admin edits the field. Always a copy, because the field's chip
 *  removal splices the array it is handed, which would otherwise be the form's own and the stored rule's. */
const toSelection = (stored: unknown): unknown[] => {
  if (isEmptySelection(stored)) {
    return [];
  }
  return Array.isArray(stored) ? [...stored] : [stored];
};

const ContextParamField: React.FC<ContextParamFieldProps> = ({ organizationId, cardId, parameter }) => {
  const { values } = useFormikContext<SlaRuleFormValues>();
  const {
    isLoading,
    isError,
    data: valueList,
  } = useDataFetcher<SlaTemplateParameterValues>(slaTemplateParamValuesUrl(organizationId, cardId, parameter.id));

  if (isLoading || isError || !valueList) {
    return <LoadingIndicator isError={isError} />;
  }

  const fieldId = `context_params.${parameter.slug}`;
  const label = TEXTS.PARAM_LABEL(parameter.name, !!parameter.required);
  const stored = values.context_params[parameter.slug];
  const isUnset = isEmptySelection(stored);

  // Metabase answers rows of `[value]` or `[value, label]`; the first cell is what a rule stores.
  const offered: ParamOption[] = (valueList.values ?? [])
    .filter((row) => !isEmpty(row))
    .map(([value, optionLabel]) => ({ value, label: String(optionLabel ?? value) }));

  if (isEmpty(offered)) {
    return (
      <div className="mb-20">
        <TextFieldFormik
          id={fieldId}
          label={label}
          type={parameter.type.startsWith('number') ? 'number' : undefined}
          // A parameter the rule has never set is absent from the form; `''` keeps the input controlled.
          value={stored ?? ''}
          helperText={isUnset ? TEXTS.PARAM_UNSET_HELPER : undefined}
          fullWidth
        />
      </div>
    );
  }

  const selection = toSelection(stored);
  // A stored value the card no longer offers stays listed and selected, so the admin sees it instead of
  // the rule's scope silently changing. Strict equality: to the save-time check '12' is not 12.
  const options: ParamOption[] = [
    ...offered,
    ...selection
      .filter((value) => !offered.some((option) => option.value === value))
      .map((value) => ({ value, label: TEXTS.PARAM_VALUE_NO_LONGER_OFFERED(String(value)) })),
  ];
  const labelByValue = new Map(options.map((option) => [option.value, option.label]));
  const helperLines = compact([
    isUnset && TEXTS.PARAM_UNSET_HELPER,
    valueList.has_more_values && TEXTS.PARAM_VALUES_TRUNCATED,
  ]);

  return (
    <div className="mb-20">
      <MultiSelectTextFieldFormik
        id={fieldId}
        label={label}
        value={selection}
        options={options.map((option) => option.value)}
        renderValue={() => ''}
        renderOption={(value: unknown) => labelByValue.get(value)}
        withOptionChips
        // An empty selection is the template's default, so it must never be filled in for the admin.
        autoSelectSingleOption={false}
        helperText={
          isEmpty(helperLines)
            ? undefined
            : helperLines.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))
        }
        fullWidth
      />
    </div>
  );
};

const CardContextParamsFields: React.FC<ContextParamsFieldsProps> = ({
  organizationId,
  cardId,
  onParametersLoaded,
}) => {
  const { values, isSubmitting, setFieldValue } = useFormikContext<SlaRuleFormValues>();
  const {
    isLoading,
    isError,
    data: metadata,
  } = useDataFetcher<SlaTemplateMetadata>(slaTemplateMetadataUrl(organizationId, cardId));

  // Held in a ref, as `useFormikChangeListener` holds its props, so the effect below runs once per card
  // whatever the identity of the callback the dialog passes.
  const onParametersLoadedRef = useRef(onParametersLoaded);
  useEffect(() => {
    onParametersLoadedRef.current = onParametersLoaded;
  }, [onParametersLoaded]);

  // Must follow the effect above, so it calls the latest callback.
  useEffect(() => {
    if (metadata) {
      onParametersLoadedRef.current(metadata.parameters ?? []);
    }
  }, [metadata]);

  if (isLoading || isError || !metadata) {
    return <LoadingIndicator isError={isError} />;
  }

  const parameters = metadata.parameters ?? [];
  const declaredSlugs = new Set(parameters.map((parameter) => parameter.slug));
  // The card was edited after the rule was saved. The daily run ignores these, but a save or preview is
  // refused by name, so they are shown with a way to drop them rather than resubmitted out of sight.
  const undeclaredSlugs = Object.keys(values.context_params).filter(
    (slug) => !declaredSlugs.has(slug) && !isEmptySelection(values.context_params[slug])
  );

  return (
    <>
      {isEmpty(undeclaredSlugs) ? null : (
        <AlertBanner
          className="mb-20"
          alertType={AlertBanner.ALERT_TYPES.WARNING}
          note={
            <div className="flex items-center justify-between gap-12">
              <span>{TEXTS.PARAMS_NO_LONGER_DECLARED(undeclaredSlugs)}</span>
              <Button
                variant="outlined"
                color="primary"
                disabled={isSubmitting}
                onClick={() => setFieldValue('context_params', omit(values.context_params, undeclaredSlugs))}
              >
                {TEXTS.REMOVE_UNDECLARED_PARAMS}
              </Button>
            </div>
          }
          withIcon
        />
      )}
      {contextParameters(parameters).map((parameter) => (
        <ContextParamField key={parameter.id} organizationId={organizationId} cardId={cardId} parameter={parameter} />
      ))}
    </>
  );
};

/** Binds `context_params.<slug>`: one field per parameter the card declares, derived from its metadata. */
const ContextParamsFields: React.FC<ContextParamsFieldsProps> = (props) => (
  // Remounted per card: `useDataFetcher` keeps the previous card's data while the next one loads, which
  // would render the old card's fields and ask the new card for the old parameters' values.
  <CardContextParamsFields key={props.cardId} {...props} />
);

export default ContextParamsFields;
